import React from "react";
import { CheckCircle, Warning, Error } from "@material-ui/icons";
import { GOOD_CONDITION_OPTION, InspectedService, mappedSnapshotData } from "./constants";
import { Job } from "../../generated/nest-graphql";

export type ColorGradeOption = "Red" | "Yellow" | "Green";

export const ColorGradeIcon: React.FC<{
  colorGrade?: ColorGradeOption | null;
  className?: string;
}> = React.memo(function ColorGradeIcon({ colorGrade, className }) {
  if (colorGrade === "Green") {
    return (
      <div className={className}>
        {" "}
        <CheckCircle fontSize="small" style={{ color: "#00C853" }} />
      </div>
    );
  }

  if (colorGrade === "Yellow") {
    return (
      <div className={className}>
        <Warning fontSize="small" style={{ color: "#FFD600" }} />
      </div>
    );
  }

  if (colorGrade === "Red") {
    return (
      <div className={className}>
        <Error fontSize="small" style={{ color: "#D50000" }} />
      </div>
    );
  }

  return null;
});

// Shared
export const getConditionColorGrade = (condition: string[]): ColorGradeOption | null => {
  if (!Array.isArray(condition)) return null;
  if (condition.length === 0) return null;
  if (condition.includes(GOOD_CONDITION_OPTION)) return "Green";
  return "Red";
};

// Fluid
export const getFluidPpmColorGrade = (ppm: string): ColorGradeOption | null => {
  if (!ppm) return null;
  const ppmNumber = Number(ppm);
  if (ppmNumber < 30) {
    return "Green";
  } else if (ppmNumber < 200) {
    return "Yellow";
  } else if (ppmNumber >= 200) {
    return "Red";
  }
  return null;
};
export const getFluidLevelColorGrade = (level: string): ColorGradeOption | null => {
  if (level === "Full") return "Green";
  if (level === "Not full") return "Red";
  return null;
};

// Pads
/**
 * Brake pad thickness grading — the one rule behind every Red/Yellow/Green a pad
 * reading gets, wherever it is shown. Matches the customer-facing gauge:
 *
 *   Red     3 mm or less      "Inspect now"
 *   Yellow  over 3, up to 6   "Monitor"
 *   Green   over 6            "Good"
 *
 * The gauge's 12 mm top is display only; a reading above it still grades Green.
 *
 * Keep in step with src/common/inspection-color-grade.ts in nubrakes-ops-backend,
 * which grades the same readings server side.
 */
export const PAD_THICKNESS_RED_MAX_MM = 3;
export const PAD_THICKNESS_YELLOW_MAX_MM = 6;

/**
 * A checklist reading as a number, or null when the technician left it blank.
 *
 * Readings arrive as strings, so blank-but-present values ("", "   ") have to be
 * told apart from a real 0: Number("") is 0, which would grade an unmeasured pad
 * Red. A genuine 0 mm reading is kept and does grade Red.
 */
export const toReadingMm = (reading: unknown): number | null => {
  if (reading === null || reading === undefined) return null;
  if (typeof reading === "string" && reading.trim() === "") return null;
  const value = Number(reading);
  return Number.isFinite(value) ? value : null;
};

export const getPadsThicknessColorGrade = (thickness: unknown): ColorGradeOption | null => {
  const mm = toReadingMm(thickness);
  if (mm === null) return null;
  if (mm <= PAD_THICKNESS_RED_MAX_MM) return "Red";
  if (mm <= PAD_THICKNESS_YELLOW_MAX_MM) return "Yellow";
  return "Green";
};

/**
 * The thinnest of several pad measurements — the one that decides the axle's grade.
 *
 * Compared as numbers. A plain .sort() is lexicographic, so an inner reading of
 * "10" and an outer of "3" put "10" first and graded the axle off its healthiest
 * measurement. Readings that are not numeric (older free-text entries) keep that
 * lexicographic order among themselves rather than being dropped.
 */
export const mostSevereMeasurement = (measurements: unknown[]): string | undefined => {
  const present = measurements.filter((m) => m !== null && m !== undefined && String(m).trim() !== "").map(String);
  if (present.length === 0) return undefined;
  const numeric = present.filter((m) => toReadingMm(m) !== null);
  if (numeric.length === 0) return present.sort()[0];
  return numeric.reduce((thinnest, m) => (Number(m) < Number(thinnest) ? m : thinnest));
};

// Battery Health
export const getCCAGrade = (coldCrankingAmps: string, CCA: string): ColorGradeOption | null => {
  if (!coldCrankingAmps || !CCA) return null;
  const coldCrankingAmpsNumber = Number(coldCrankingAmps);
  const CCANumber = Number(CCA);
  const result = (coldCrankingAmpsNumber * 100) / CCANumber;
  if (Number.isNaN(result)) return null;
  if (result < 50) {
    return "Red";
  } else if (result >= 50 && result <= 60) {
    return "Yellow";
  } else if (result > 60) {
    return "Green";
  }
  return null;
};

export const getBatteryHealthGrade = (batteryHealth: string): ColorGradeOption | null => {
  if (!batteryHealth) return null;
  const batteryHealthNumber = Number(batteryHealth);
  if (Number.isNaN(batteryHealthNumber)) return null;
  if (batteryHealthNumber < 65) {
    return "Red";
  } else if (batteryHealthNumber >= 65 && batteryHealthNumber < 80) {
    return "Yellow";
  } else if (batteryHealthNumber >= 80) {
    return "Green";
  }
  return null;
};

// Rotors
export const getRotorsThicknessColorGrade = (
  thickness: string | null,
  discardThickness: string | null = null
): ColorGradeOption | null => {
  if (!thickness) return null;
  const thicknessNumber = Number(thickness);
  if (Number.isNaN(thicknessNumber)) return null;

  if (!discardThickness) return null;
  const discardThicknessNumber = Number(discardThickness);
  if (Number.isNaN(discardThicknessNumber)) return null;

  if (thicknessNumber <= discardThicknessNumber) {
    return "Red";
  } else {
    return "Green";
  }
};

export const ColorGradeMap = (colorGrade?: ColorGradeOption[] | null): ColorGradeOption => {
  if (colorGrade.includes("Red")) return "Red";
  if (colorGrade.includes("Yellow")) return "Yellow";
  return "Green";
};

interface GetStatusProps {
  thickness?: string | number;
  condition?: string[];
  colorGrade?: ColorGradeOption;
  CCA?: number;
  job: Job;
  typeName: string;
  coldCrankingAmps?: string;
  batteryHealth?: string;
}

export const getStatus = ({
  thickness,
  condition,
  coldCrankingAmps,
  CCA,
  colorGrade,
  batteryHealth,
  job,
  typeName,
}: GetStatusProps) => {
  let thicknessColorGrade, conditionColorGrade, CCAColorGrade, batteryHealthColorGrade;
  if (thickness) {
    if (typeName === "CheckListV2BrakeFluidInfo") {
      thicknessColorGrade = isNaN(Number(thickness))
        ? getFluidLevelColorGrade(thickness as string)
        : getFluidPpmColorGrade(thickness as string);
    }
    if (typeName === "CheckListV2RotorsInfo") {
      thicknessColorGrade = getRotorsThicknessColorGrade(
        thickness as string,
        job?.vehicleInfo?.frontRotorsDiscardThicknessInches?.toString()
      );
    }
    if (["CheckListV2PadsInfo", "BrakePadsListItem"].includes(typeName)) {
      thicknessColorGrade = getPadsThicknessColorGrade(thickness as string);
    }
  }
  if (condition) conditionColorGrade = getConditionColorGrade(condition);
  if (coldCrankingAmps && CCA) CCAColorGrade = getCCAGrade(coldCrankingAmps, CCA.toString());
  if (batteryHealth) batteryHealthColorGrade = getBatteryHealthGrade(batteryHealth);

  return ColorGradeMap([thicknessColorGrade, conditionColorGrade, colorGrade, CCAColorGrade, batteryHealthColorGrade]);
};

export const groupServicesByGrade = (
  job?: Job
): {
  immediatly?: InspectedService[];
  soon?: InspectedService[];
  good?: InspectedService[];
} => {
  const checkList = job?.preJobCheckListV2;
  if (!checkList) return {};
  const immediatlyServices = [];
  const soonServices = [];
  const goodServices = [];
  Object.entries({
    ...checkList,
    ...checkList.inspectedServices,
    battery: {
      __typename: "battery" as const,
      CCA: job?.vehicle?.CCA,
      coldCrankingAmps: checkList.coldCrankingAmps,
      batteryHealth: checkList.batteryHealth,
    },
  }).forEach(([key, service]) => {
    if (
      typeof service === "string" ||
      typeof service === "boolean" ||
      service?.__typename === "CheckListV2InspectedServices"
    )
      return;

    const config = mappedSnapshotData.find(([itemKey]) => itemKey.includes(key))?.[1];
    if (!config) return;
    let thickness, colorGrade, condition, CCA, coldCrankingAmps, batteryHealth;
    if (service.__typename === "battery" && service.batteryHealth != null) {
      batteryHealth = service.batteryHealth;
    } else if (
      service.__typename === "CheckListV2PadsInfo" &&
      (service.condition?.length ||
        service.innerPadThickness ||
        service.outerPadThickness ||
        service.padsThickness ||
        service.driverSide ||
        service.passengerSide)
    ) {
      thickness = mostSevereMeasurement([
        service.innerPadThickness,
        service.outerPadThickness,
        service.padsThickness,
        service.driverSide,
        service.passengerSide,
      ]);
      condition = service.condition;
    } else if (
      service.__typename === "CheckListV2RotorsInfo" &&
      (service.condition?.length || service.thicknessInches)
    ) {
      thickness = service.thicknessInches;
      condition = service.condition;
    } else if (
      service.__typename === "CheckListV2BrakeFluidInfo" &&
      (service.colorGrade || service.level || service.ppm)
    ) {
      thickness = service.level;
      colorGrade = service.colorGrade;
    } else if (
      (service.__typename === "CheckListV2DrumsInfo" || service.__typename === "CheckListV2ShoesInfo") &&
      (service.colorGrade || service.condition?.length)
    ) {
      colorGrade = service.colorGrade;
      condition = service.condition;
    } else if (service.__typename === "CheckListV2InspectedServicesItem" && (service.colorGrade || service.notes)) {
      colorGrade = service.colorGrade;
      thickness = service.notes;
    } else return;
    const status = getStatus({
      job,
      thickness,
      CCA,
      coldCrankingAmps,
      colorGrade,
      condition,
      batteryHealth,
      typeName: service.__typename,
    });
    const newService = {
      ...service,
      config,
      status,
    };
    if (newService.status === "Red") {
      immediatlyServices.push(newService);
    } else if (newService.status === "Yellow") {
      soonServices.push(newService);
    } else {
      goodServices.push(newService);
    }
  });
  return {
    immediatly: immediatlyServices,
    soon: soonServices,
    good: goodServices,
  };
};
